import { MODULES, moduleSpec } from '@yourstore/domain';
import type { T } from '../../../messages';
import { featureName, featureTitle } from '../features';

/**
 * **The feature list** — `@yourstore/domain`'s module table, arranged by what
 * each module does for the business instead of in spec order.
 *
 * Arrangement only. What a module depends on is still `MODULES[].dependsOn`,
 * read here and nowhere copied; what a request may do is still the server's
 * refusal. The helpers below compute the *offer* — "Website needs Texts and
 * emails; turn on both?" — so the operator is asked the right question before
 * the request, and `admin-api`'s `409` stays the authority behind it (a stale
 * page, a second operator, a newer server).
 *
 * The groups, and the two placements that needed a decision:
 *
 *  * **Running the day** is the three foundation modules as one row, "Always
 *    on" once all three are: nothing else works without them, and three
 *    switches that must never be off one at a time are three ways to break a
 *    business. They can still be turned off, together, from the row's menu.
 *  * **Online payments** (`billing`) sits under *Selling*, not under a
 *    "Platform" heading: to the owner it is how money comes in online, and
 *    three features she sells with (shop prepay, deposits, merch) need it.
 *  * **Booking partners** (`partner_api`) sits under *Reaching clients*: it is
 *    how the booking assistant and partners bring her clients. A "Platform"
 *    group would name our plumbing rather than her job.
 *  * **Other kinds of business** — ordering, lodging, departures, and the two
 *    built on them (tour photos, merch) — fold away unless one is on
 *    (principle 6). `admin`, the one "platform, never sold" module, folds with
 *    them under *Behind the scenes*, so it stays reachable without being shown
 *    to every spa.
 *
 * A module the domain adds later and this file has not placed lands under
 * *Behind the scenes* with the domain's own label, so it is never unreachable.
 */

export type FeatureGroup = 'day' | 'selling' | 'reaching' | 'kinds' | 'behind';

export const FEATURE_GROUPS: readonly FeatureGroup[] = [
  'day',
  'selling',
  'reaching',
  'kinds',
  'behind',
];

/** The groups that fold away unless something in them is on. */
export const FOLDED_GROUPS: ReadonlySet<FeatureGroup> = new Set(['kinds', 'behind']);

export interface FeatureRow {
  /** `foundation` for the composite row, else the module's own slug. */
  id: string;
  /** The modules the row switches — three for the foundation, one otherwise. */
  keys: readonly string[];
  group: FeatureGroup;
}

export const FOUNDATION: readonly string[] = ['clients', 'services', 'calendar'];

const PLACED: readonly FeatureRow[] = [
  { id: 'foundation', keys: FOUNDATION, group: 'day' },
  { id: 'sales', keys: ['sales'], group: 'selling' },
  { id: 'products', keys: ['products'], group: 'selling' },
  { id: 'certificates', keys: ['certificates'], group: 'selling' },
  { id: 'credit', keys: ['credit'], group: 'selling' },
  { id: 'billing', keys: ['billing'], group: 'selling' },
  { id: 'reporting', keys: ['reporting'], group: 'selling' },
  { id: 'booking_site', keys: ['booking_site'], group: 'reaching' },
  { id: 'notifications', keys: ['notifications'], group: 'reaching' },
  { id: 'partner_api', keys: ['partner_api'], group: 'reaching' },
  { id: 'ordering', keys: ['ordering'], group: 'kinds' },
  { id: 'lodging', keys: ['lodging'], group: 'kinds' },
  { id: 'departures', keys: ['departures'], group: 'kinds' },
  { id: 'photos', keys: ['photos'], group: 'kinds' },
  { id: 'fulfillment', keys: ['fulfillment'], group: 'kinds' },
  { id: 'admin', keys: ['admin'], group: 'behind' },
];

/** Every module the domain knows, each in exactly one row. */
export const FEATURE_ROWS: readonly FeatureRow[] = [
  // Only the rows whose modules the domain still has.
  ...PLACED.filter((row) => row.keys.every((k) => moduleSpec(k) !== undefined)),
  ...MODULES.filter((m) => !PLACED.some((row) => row.keys.includes(m.key))).map(
    (m): FeatureRow => ({ id: m.key, keys: [m.key], group: 'behind' }),
  ),
];

export function rowOf(module: string): FeatureRow | undefined {
  return FEATURE_ROWS.find((row) => row.keys.includes(module));
}

/* ------------------------------------------------------------------ */
/* The dependency graph, walked                                        */
/* ------------------------------------------------------------------ */

/**
 * Everything `keys` needs that is not in `enabled`, dependencies first — the
 * order a person would switch them on in, and an order the server accepts
 * (it validates the state the request leaves behind, so any order would do).
 */
export function missingFor(enabled: ReadonlySet<string>, keys: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const visit = (key: string): void => {
    if (seen.has(key)) return;
    seen.add(key);
    for (const dep of moduleSpec(key)?.dependsOn ?? []) visit(dep);
    if (!enabled.has(key) && !keys.includes(key)) out.push(key);
  };
  for (const key of keys) visit(key);
  return out;
}

/** Every enabled module that would be left needing one of `keys` — all the way down. */
export function dependentsFor(enabled: ReadonlySet<string>, keys: readonly string[]): string[] {
  const going = new Set(keys);
  const out: string[] = [];
  let grew = true;
  while (grew) {
    grew = false;
    for (const spec of MODULES) {
      if (!enabled.has(spec.key) || going.has(spec.key)) continue;
      if (spec.dependsOn.some((d) => going.has(d))) {
        going.add(spec.key);
        out.push(spec.key);
        grew = true;
      }
    }
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Words                                                               */
/* ------------------------------------------------------------------ */

/**
 * `as const` so `t(WORDS[k].what)` is a checked catalog key. A row is named by its
 * feature (`../features.ts`, the console's one set of feature names); only the
 * foundation row, three modules speaking as one, has a name of its own here.
 */
const WORDS = {
  foundation: {
    what: 'setup.feature.foundation.what',
    gone: 'setup.feature.foundation.gone',
  },
  sales: {
    what: 'setup.feature.sales.what',
    gone: 'setup.feature.sales.gone',
  },
  products: {
    what: 'setup.feature.products.what',
    gone: 'setup.feature.products.gone',
  },
  certificates: {
    what: 'setup.feature.certificates.what',
    gone: 'setup.feature.certificates.gone',
  },
  credit: {
    what: 'setup.feature.credit.what',
    gone: 'setup.feature.credit.gone',
  },
  reporting: {
    what: 'setup.feature.reporting.what',
    gone: 'setup.feature.reporting.gone',
  },
  billing: {
    what: 'setup.feature.billing.what',
    gone: 'setup.feature.billing.gone',
  },
  booking_site: {
    what: 'setup.feature.booking_site.what',
    gone: 'setup.feature.booking_site.gone',
  },
  notifications: {
    what: 'setup.feature.notifications.what',
    gone: 'setup.feature.notifications.gone',
  },
  partner_api: {
    what: 'setup.feature.partner_api.what',
    gone: 'setup.feature.partner_api.gone',
  },
  ordering: {
    what: 'setup.feature.ordering.what',
    gone: 'setup.feature.ordering.gone',
  },
  lodging: {
    what: 'setup.feature.lodging.what',
    gone: 'setup.feature.lodging.gone',
  },
  departures: {
    what: 'setup.feature.departures.what',
    gone: 'setup.feature.departures.gone',
  },
  photos: {
    what: 'setup.feature.photos.what',
    gone: 'setup.feature.photos.gone',
  },
  fulfillment: {
    what: 'setup.feature.fulfillment.what',
    gone: 'setup.feature.fulfillment.gone',
  },
  admin: {
    what: 'setup.feature.admin.what',
    gone: 'setup.feature.admin.gone',
  },
} as const;

function words(id: string): (typeof WORDS)[keyof typeof WORDS] | undefined {
  return (WORDS as Record<string, (typeof WORDS)[keyof typeof WORDS]>)[id];
}

/** A row's name: the foundation's own, else its feature's title. */
export function rowName(t: T, row: FeatureRow): string {
  return row.id === 'foundation' ? t('setup.feature.foundation.name') : featureTitle(t, row.id);
}

export function rowWhat(t: T, row: FeatureRow): string {
  const w = words(row.id);
  return w === undefined ? '' : t(w.what);
}

export function rowGone(t: T, row: FeatureRow): string {
  const w = words(row.id);
  return w === undefined ? rowName(t, row) : t(w.gone);
}

/** One module's name, for a dependency list — "needs the calendar and Checkout". */
export function moduleName(t: T, key: string): string {
  if (FOUNDATION.includes(key)) return featureName(t, key);
  const row = rowOf(key);
  return row === undefined ? key : rowName(t, row);
}

/** Module slugs to names, one per row, in the order given. */
export function namesOf(t: T, keys: readonly string[]): string {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const key of keys) {
    // A foundation dependency is named by the module, not the three-way row.
    const id = FOUNDATION.includes(key) ? key : (rowOf(key)?.id ?? key);
    if (seen.has(id)) continue;
    seen.add(id);
    names.push(moduleName(t, key));
  }
  return names.join(', ');
}
